// src/pages/Vendors.js
import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import Cache from "../utils/cache";
import { normalizeVendorName } from "../utils/vendorMatchUtils";

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadVendors = async () => {
      let cached = Cache.getData().vendors;

      // Check cache validity: vendors should be an array of vendor objects, not grouped docs
      const cacheValid =
        cached &&
        Array.isArray(cached) &&
        cached.length > 0 &&
        typeof cached[0] === "object" &&
        (cached[0].vendorName || cached[0].id);

      if (cacheValid) {
        setVendors(cached);
        setFilteredVendors(cached);
        setLoading(false);
        return;
      }

      try {
        const vendorsCollection = collection(db, "vendors");
        const snapshot = await getDocs(vendorsCollection);

        // Filter to only grouped docs with id starting with "vendors_"
        const groupedDocs = snapshot.docs.filter((doc) =>
          doc.id.startsWith("vendors_")
        );

        // Flatten all vendor arrays from grouped docs
        const allVendors = [];
        groupedDocs.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.vendors)) {
            data.vendors.forEach((vendor) => {
              if (!vendor.vendorName && vendor.id) {
                vendor.vendorName = vendor.id;
              }
              allVendors.push(vendor);
            });
          }
        });

        // Cache and set state with flattened vendor array
        const existingCache = Cache.getData();
        Cache.setData({
          ...existingCache,
          vendors: allVendors,
        });

        setVendors(allVendors);
        setFilteredVendors(allVendors);
      } catch (err) {
        console.error("Error fetching vendors from Firestore:", err);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, []);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    let results = [...vendors];

    if (searchTerm.trim()) {
      const normalizedSearch = normalizeVendorName(searchTerm);

      results = results.filter((vendor) => {
        const rawName = vendor.vendorName || vendor.id || "";
        const rawCategories = vendor.categories || vendor.category || [];

        const normalizedName = normalizeVendorName(rawName);
        const nameMatch = normalizedName.includes(normalizedSearch);

        const categoryMatch = Array.isArray(rawCategories) && rawCategories.some(
          (cat) =>
            typeof cat === "string" &&
            normalizeVendorName(cat).includes(normalizedSearch)
        );

        return nameMatch || categoryMatch;
      });
    }

    results.sort((a, b) => {
      const nameA = (a.vendorName || a.id || "").toLowerCase();
      const nameB = (b.vendorName || b.id || "").toLowerCase();
      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    setFilteredVendors(results);
  }, [searchTerm, sortOrder, vendors]);

  // Reset page to 1 when filters/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, filteredVendors]);

  const totalPages = Math.ceil(filteredVendors.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedVendors = filteredVendors.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="max-w-6xl mx-auto px-4">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">All Vendors</h1>

      {/* Search + Sort */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
        <input
          type="text"
          placeholder="Search by name or category..."
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="px-3 py-2 border border-gray-300 rounded shadow-sm"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="asc">Sort A → Z</option>
          <option value="desc">Sort Z → A</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center text-gray-600">Loading vendors...</div>
      ) : filteredVendors.length === 0 ? (
        <div className="text-center text-gray-500">No vendors found.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {paginatedVendors.map((vendor, idx) => {
              const displayName = vendor.vendorName || vendor.id || `Vendor ${startIndex + idx}`;

              return (
                <div
                  key={displayName + (startIndex + idx)}
                  onClick={() => navigate(`/vendor/${displayName}`)}
                  className="cursor-pointer bg-white shadow p-4 rounded text-center hover:shadow-lg transition"
                >
                  {vendor.logo ? (
                    <>
                      <img
                        src={vendor.logo}
                        alt={displayName}
                        className="mx-auto h-16 object-contain mb-2"
                      />
                      <div className="text-sm font-semibold text-gray-800">{displayName}</div>
                    </>
                  ) : (
                    <div
                      className="font-bold text-gray-900 mb-2 select-none"
                      style={{
                        fontSize: "clamp(1rem, 4vw, 2rem)",
                        lineHeight: 1.1,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        maxWidth: "90%",
                        margin: "0 auto",
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </div>
                  )}

                  {vendor.categories && vendor.categories.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {vendor.categories.join(", ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination controls */}
          <div className="flex justify-center items-center gap-3 mt-6 mb-10">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() =>
                setCurrentPage((p) =>
                  p < totalPages ? p + 1 : p
                )
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
